import React, { useState, useRef, useEffect } from "react";
import { Input } from "antd";
import { auth } from "../../API/Auth";

function NotiDetailedTemplate(props: {
  report?: any;
  distribution?: any;
  additionalText?: any;
  onAdditionalTextChange?: any;
  notificationTemplate?: string | null;
  onMainBodyChange?: (body: { html: string; text: string }) => void;
  showAdditionalText?: boolean;
  isEditable?: boolean;
}) {
  const { TextArea } = Input;
  const report = props.report || {};
  const {
    type,
    typeValue,
    org,
    product,
    studyTitle,
    studyID,
    safetyDatabaseReportNumber,
    notificationTemplateHtml,
  } = report;

  const distribution = props.distribution || {};
  const { name, studyNumber } = distribution;
  const { additionalText, showAdditionalText, onAdditionalTextChange } = props;

  const template =
    notificationTemplateHtml || report.notificationTemplateHtml || props.notificationTemplate;

  const editableRef = useRef<HTMLDivElement | null>(null);
  const didSetInitialHtml = useRef<boolean>(false);
  const isEditable = props.isEditable === undefined ? true : props.isEditable;

  // If a template is provided, set the editable HTML once when template/additionalText change
  useEffect(() => {
    if (template && editableRef.current) {
      const computedHtml =
        additionalText && !(showAdditionalText && auth.isPermissionExists(["0"]))
          ? template.replace("<wbr></wbr>", `<p>${additionalText}</p>`)
          : template.replace("<wbr></wbr>", ``);
      editableRef.current.innerHTML = computedHtml;
      didSetInitialHtml.current = true;
      const html = editableRef.current.innerHTML;
      const text = editableRef.current.innerText;
      props.onMainBodyChange && props.onMainBodyChange({ html, text });
    }
  }, [template, additionalText, showAdditionalText]);

  // Default template: initialize innerHTML once on mount so user edits are not overwritten by re-renders
  useEffect(() => {
    if (editableRef.current && !didSetInitialHtml.current) {
      const html = editableRef.current.innerHTML;
      const text = editableRef.current.innerText;
      props.onMainBodyChange && props.onMainBodyChange({ html, text });
      didSetInitialHtml.current = true;
    }
    // run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = (e: any) => {
    const html = e.currentTarget.innerHTML;
    const text = e.currentTarget.innerText;
    props.onMainBodyChange && props.onMainBodyChange({ html, text });
  };

  const handleDefaultChange = (e: any) => {
    const html = e.currentTarget.innerHTML;
    const text = e.currentTarget.innerText;
    props.onMainBodyChange && props.onMainBodyChange({ html, text });
  };

  // If a template is provided, render it and report initial + subsequent content
  if (template) {
    const computedHtml =
      additionalText && !(showAdditionalText && auth.isPermissionExists(["0"]))
        ? template.replace("<wbr></wbr>", `<p>${additionalText}</p>`)
        : template.replace("<wbr></wbr>", ``);

    

    return (
      <>
        <div
          contentEditable={isEditable}
          suppressContentEditableWarning
          onInput={handleTemplateChange}
          onBlur={() => {
            if (editableRef.current) {
              const html = editableRef.current.innerHTML;
              const text = editableRef.current.innerText;
              props.onMainBodyChange && props.onMainBodyChange({ html, text });
            }
          }}
          ref={editableRef}
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            minHeight: "200px",
          }}
        />

        {showAdditionalText && auth.isPermissionExists(["0"]) && (
          <TextArea
            onChange={(event) => onAdditionalTextChange && onAdditionalTextChange(event.target.value)}
            style={{
              marginTop: "1rem",
              marginBottom: "1rem",
              height: "120px",    // these three line height, minHeight, resize are for textarea's initial height so that it do not distorts the template initially
              minHeight: "120px",
              resize: "vertical",
            }}
            showCount
            maxLength={2000}
            placeholder="Add any additional information, as required"
          />
        )}
      </>
    );
  }

  
  return (
    <div
      contentEditable={isEditable}
      suppressContentEditableWarning
      onInput={handleDefaultChange}
      ref={editableRef}
      style={{
        border: "1px solid #ddd",
        padding: "1rem",
        borderRadius: "8px",
        minHeight: "200px",
      }}
    >
      <p>Dear {name || "{Name}"}</p>
      <p>
        {org} is the Sponsor of the clinical study {studyID || "{Study ID}"} -{" "}
        {studyTitle || "{Study Title}"}. {org} is required to inform all
        participating Investigators in writing of any suspected, unexpected
        serious adverse reaction (SUSAR) that occurs in a study evaluating the
        investigational product. Soterius, on behalf of{" "}
        {org || "{Sponsor Name}"}, is providing you with a suspected unexpected
        serious adverse reaction (SUSAR) from study {studyID} for {product}.
      </p>

      {showAdditionalText && auth.isPermissionExists(["0"]) ? (
        <TextArea
          onChange={(event) => onAdditionalTextChange && onAdditionalTextChange(event.target.value)}
          style={{ marginBottom: "1rem" }}
          showCount
          maxLength={2000}
          placeholder="Add any additional information, as required"
        />
      ) : (
        <p>{additionalText}</p>
      )}

      <p>
        Enclosed, please find the following documents with regards to this{" "}
        {type == "100" ? typeValue : `${type}-day SUSAR`}: {safetyDatabaseReportNumber}:
      </p>

      <p style={{ paddingLeft: "1.5rem" }}>
        <ol>
          <li>Investigator Notification Letter</li>
          <li>CIOMS/MedWatch Form </li>
        </ol>
      </p>

      <p>
        Please review the attached CIOMS/MedWatch form for this case to evaluate
        the risks and benefits of the study{" "}
        {studyNumber ? `(${studyNumber})` : `({Study Number})`} at your
        investigational site. Additionally, also file the documents in the
        regulatory section of your study binder and please report this
        information to your Institutional Review Board or Ethics Committee in
        accordance with applicable laws and regulations.
      </p>

      <p>
        Thank you very much and feel free to reach out to the Safety Team /
        Medical Monitor as per the Contact Information provided by {org}, should
        you have any questions.
      </p>

      <p>
        Note: Request you to kindly provide your acknowledgement of receipt of
        this communication and also provide the confirmation of the notification
        to EC/IRB.
      </p>

      <p>{additionalText || ""}</p>

      <p>Kind regards,</p>
      <p>Soterius Safety Team</p>
      <p>On behalf of {org}</p>
    </div>
  );
}

export default NotiDetailedTemplate;
